import { AnyDate, AnyPeriod, AnyPrice, TranslatedString } from "@ct-react/core";
import { FormattedBookingOption } from "@ct-react/calendar";
import { ArticleDataUrl } from "@shared/models/urls";
import { Image } from "./medias";

export type TagsData = {
  novelty: boolean;
  recommended: boolean;
  sold?: boolean;
}

export type ResumeFeaturesData = {
  livingSpace?: number;
  rooms?: number;
  maxGuests?: number;
  bedrooms?: number;
  parkings?: number;
};

export type Discount = {
  logic: string;
  period: AnyPeriod;
  type: string;
  fixedValue: AnyPrice;
  percentageValue: number;
};

type SeasonalPrice = {
  period: AnyPeriod;
  isMonthly: boolean;
  onDemand: boolean;
  value: AnyPrice;
};

type RentalLowestPrice = {
  type: "DAILY" | "WEEKLY";
  onDemand: boolean;
  value: AnyPrice;
}

type AnnualPrice = {
  availableAt: AnyDate;
  onDemand: boolean;
  value: AnyPrice;
}

type SalePrice = {
  onDemand: boolean;
  value: AnyPrice;
}

type PriceResumeType = FormattedBookingOption | SeasonalPrice | SalePrice | AnnualPrice | RentalLowestPrice;

export type ResumeBookChoice = {
  title: string;
  address: string;
  thumbnail?: Image;
  price?: PriceResumeType
}

export type ArticleSummary = ArticleDataUrl & {
  __typename: string;
  title: TranslatedString,
  images: Image[];
  features: ResumeFeaturesData;
  novelty: boolean;
  recommended: boolean;
}

export type RentalArticleSummary = ArticleSummary & {
  discounts?: Discount[];
  bookingSuggestion?: FormattedBookingOption;
}

export type SeasonalArticleSummary = ArticleSummary & {
  price?: SeasonalPrice;
}

export type AnnualArticleSummary = ArticleSummary & {
  price?: AnnualPrice;
}

export type SaleArticleSummary = ArticleSummary & {
  sold: boolean;
  price?: SalePrice;
}

export type AnyArticleSummary =
  RentalArticleSummary
  | SeasonalArticleSummary
  | AnnualArticleSummary
  | SaleArticleSummary;

export const isRentalArticleSummary = (value: AnyArticleSummary): value is RentalArticleSummary =>
  value.__typename === "RentalAccommodationArticle";

export const isSeasonalArticleSummary = (value: AnyArticleSummary): value is SeasonalArticleSummary =>
  value.__typename === "SeasonalAccommodationArticle";

export const isAnnualArticleSummary = (value: AnyArticleSummary): value is AnnualArticleSummary =>
  value.__typename === "AnnualAccommodationArticle";

export const isSaleArticleSummary = (value: AnyArticleSummary): value is SaleArticleSummary =>
  value.__typename === "SaleAccommodationArticle";

export const isBookingSuggestionPrice = (value?: PriceResumeType): value is FormattedBookingOption =>
  !!value && "period" in value && "price" in value;

export const isRentalLowestPrice = (value?: PriceResumeType): value is RentalLowestPrice =>
  !!value && "type" in value && "onDemand" in value && !value.onDemand;

export const isSeasonalPrice = (value?: PriceResumeType): value is SeasonalPrice =>
  !!value && "isMonthly" in value && "onDemand" in value && !value.onDemand;

export const isAnnualPrice = (value?: PriceResumeType): value is AnnualPrice =>
  !!value && "availableAt" in value && "onDemand" in value && !value.onDemand;

export const isSalePrice = (value?: PriceResumeType): value is SalePrice =>
  !!value && "onDemand" in value && !value.onDemand;
